import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    TextField,
    FormControl,
    CircularProgress,
    makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import type { COUNTRY_MENU_OPTION } from '@klp/leverandor/types';
import { COUNTRY_WITH_API_SUPPORT } from '@klp/leverandor/types';
import {
    useGetCompanyBySearchQuery,
    useGetLandKodeverkQuery,
} from '@klp/leverandor/services/rtkApi';
import type { CompanySearchResponse } from '@klp/leverandor/services/rtkApi';
import { handleOrgnummerValidering } from '@klp/formgenerator/utils';
import useDebounce from '@forvalter/utils/useDebounce';
import {
    isCountryWithApiSupport,
    mapOrganizationCountryToRegion,
} from '@klp/leverandor/utils';
import { CountryAdornment } from './CountrySelect/CountryAdornment';
import { CountryMenu } from './CountrySelect/CountryMenu';

const useStyles = makeStyles((theme) => ({
    formField: {
        '& .MuiTypography-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
}));

export interface OrganizationSelectProps {
    initialCountry: COUNTRY_WITH_API_SUPPORT;
    touched?: boolean;
    onChange: (orgnummer: string | undefined) => void;
    onCountryChange: (
        country: COUNTRY_MENU_OPTION,
        soCountryId: string | undefined,
    ) => void;
    onNameChange: (name: string | undefined) => void;
}

export const OrganizationSelect = ({
    initialCountry,
    touched,
    onChange,
    onCountryChange,
    onNameChange,
}: OrganizationSelectProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [country, setCountry] = useState<COUNTRY_MENU_OPTION>(initialCountry);
    const [selectedCompany, setSelectedCompany] =
        useState<CompanySearchResponse | null>(null);
    const [componentValidationError, setComponentValidationError] =
        useState<string>();
    const [countryMenuAnchor, setCountryMenuAnchor] =
        useState<null | HTMLElement>(null);
    const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);

    const { data } = useGetLandKodeverkQuery({ locale: 'en' });
    const { debouncedValue: debouncedInputValue, isDebouncing: isDebouncing } =
        useDebounce(inputValue, 1000);

    const {
        data: companyOptions = [],
        isLoading: companiesIsLoading,
        isFetching: companiesIsFetching,
    } = useGetCompanyBySearchQuery(
        {
            searchValue: debouncedInputValue,
            country: country as COUNTRY_WITH_API_SUPPORT,
        },
        {
            skip:
                !isCountryWithApiSupport(country) ||
                debouncedInputValue.length <= 2 ||
                !!componentValidationError,
        },
    );

    useEffect(() => {
        if (companyOptions.length === 1) {
            handleCompanyChanged(companyOptions[0]);
            setAutocompleteOpen(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyOptions]);

    // Reset component error when user starts typing
    useEffect(() => {
        setComponentValidationError(undefined);
        if (inputValue.length <= 2 || !isCountryWithApiSupport(country)) {
            return;
        }

        const formattedInput = inputValue.replace(/[\s-]+/g, '');
        const isOrgnummer = /^\d+$/.test(formattedInput);
        const shouldValidateOrgnummer =
            formattedInput.length > 0 &&
            (isOrgnummer || country === COUNTRY_WITH_API_SUPPORT.Sweden);
        const validOrgnummer = handleOrgnummerValidering(
            formattedInput,
            mapOrganizationCountryToRegion(country),
        );
        if (shouldValidateOrgnummer && !validOrgnummer) {
            setComponentValidationError(t('project.errors.invalidOrgnumber'));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, country]);

    // Memoized computation of displayed company name
    const getDisplayedCompanyName = useMemo(() => {
        if (selectedCompany) {
            return `${selectedCompany.navn} (${selectedCompany.organisasjonsnummer})`;
        }
        return inputValue;
    }, [selectedCompany, inputValue]);

    // Handle company selection and update form
    const handleCompanyChanged = useCallback(
        (company: CompanySearchResponse | null) => {
            if (company) {
                setSelectedCompany(company);
                onChange(company.organisasjonsnummer);
                onNameChange(company.navn ?? undefined);
            } else {
                setSelectedCompany(null);
                onChange(undefined);
                onNameChange(undefined);
                setInputValue('');
            }
        },
        [onChange, onNameChange],
    );

    const isLoading = companiesIsLoading || companiesIsFetching || isDebouncing;
    const displayError = componentValidationError;

    const handleCountrySelect = (selectedCountry: COUNTRY_MENU_OPTION) => {
        setCountry(selectedCountry);
        const soCountryId = data
            ? Object.entries(data).find(
                  ([_, value]) => value === selectedCountry,
              )?.[0]
            : undefined;
        onCountryChange(selectedCountry, soCountryId);

        setSelectedCompany(null);
        onChange(undefined);
        onNameChange(undefined);

        setCountryMenuAnchor(null);
    };

    const placeholderText = useMemo(() => {
        if (country === COUNTRY_WITH_API_SUPPORT.Sweden) {
            return t('leverandor.createLeverandor.orgnummer.placeholderSweden');
        }
        return t('leverandor.createLeverandor.orgnummer.placeholder');
    }, [t, country]);

    return (
        <FormControl fullWidth={true} error={touched && !!displayError}>
            <Autocomplete<CompanySearchResponse>
                className={classes.formField}
                id="orgnummerAutocomplete"
                fullWidth={true}
                clearOnBlur={true}
                handleHomeEndKeys={true}
                open={autocompleteOpen && !componentValidationError}
                onOpen={() => setAutocompleteOpen(true)}
                onClose={() => setAutocompleteOpen(false)}
                value={selectedCompany}
                onInputChange={(_event, newInputValue, reason) => {
                    if (reason === 'input') {
                        setInputValue(newInputValue);
                    }
                }}
                onChange={(_event, newValue) => {
                    handleCompanyChanged(newValue);
                }}
                getOptionSelected={(option, val) =>
                    option.organisasjonsnummer === val.organisasjonsnummer
                }
                getOptionLabel={(option) =>
                    `${option.navn} (${option.organisasjonsnummer})`
                }
                options={companyOptions}
                loading={isLoading}
                inputValue={getDisplayedCompanyName}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        disabled={!country}
                        required={true}
                        label={t('leverandor.createLeverandor.orgnummer.label')}
                        placeholder={placeholderText}
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <CountryAdornment
                                    country={country}
                                    setCountryMenuAnchor={setCountryMenuAnchor}
                                />
                            ),
                            endAdornment: (
                                <>
                                    {isLoading && (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    )}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        inputProps={{
                            ...params.inputProps,
                            'data-testid': 'orgnummer-search-input',
                        }}
                        error={!!displayError}
                        helperText={displayError ? displayError : undefined}
                    />
                )}
            />
            <CountryMenu
                country={country}
                countryMenuAnchor={countryMenuAnchor}
                setCountryMenuAnchor={setCountryMenuAnchor}
                handleCountrySelect={handleCountrySelect}
            />
        </FormControl>
    );
};
